import type { AnnotationItem } from '/@/types/annotation'
import type { LabelSelectOption, LabelStore } from '/@/composables/image-annotator/useLabelStore'

export interface AnnotationLabelPayload {
  labelCn: string
  labelEn: string
}

export function hasAnnotationLabel (
  ann: Pick<AnnotationItem, 'label' | 'labelCn' | 'labelEn'>
): boolean {
  return !!(ann.labelCn || ann.labelEn || ann.label)
}

export function getAnnotationDisplayLabel (
  ann: Pick<AnnotationItem, 'label' | 'labelCn' | 'labelEn'>,
  locale: string,
  labelStore?: Pick<LabelStore, 'findByEn'>
): string {
  const isZh = locale.startsWith('zh')
  if (isZh) {
    // 优先使用已有的中文标签，如果没有则通过英文标签查找中文标签
    if (ann.labelCn) return ann.labelCn
    if (ann.label && labelStore) {
      const found = labelStore.findByEn(ann.label)
      if (found) return found.labelCn
    }
    if (ann.labelEn && labelStore) {
      const found = labelStore.findByEn(ann.labelEn)
      if (found) return found.labelCn
    }
    return ann.label || ann.labelEn || ''
  }
  // 英文环境优先使用英文标签，如果没有则使用中文标签查找英文标签
  if (ann.labelEn) return ann.labelEn
  if (ann.labelCn && labelStore) {
    const found = labelStore.findByCn(ann.labelCn)
    if (found) return found.labelEn
  }
  return ann.label || ann.labelCn || ''
}

export function getAnnotationSelectValue (
  ann: Pick<AnnotationItem, 'label' | 'labelCn' | 'labelEn'>,
  findByEn: (labelEn: string) => LabelSelectOption | undefined,
  findByCn: (labelCn: string) => LabelSelectOption | undefined
): string | null {
  if (ann.labelEn) return ann.labelEn
  const cn = ann.labelCn || ann.label
  if (!cn) return null
  return findByCn(cn)?.value ?? null
}

export function syncAnnotationLabelFields (
  payload: AnnotationLabelPayload
): AnnotationLabelPayload & { label: string } {
  return {
    labelCn: payload.labelCn,
    labelEn: payload.labelEn,
    label: payload.labelCn || payload.labelEn,
  }
}

export function toLabelStudioLabelArray (
  ann: Pick<AnnotationItem, 'label' | 'labelCn' | 'labelEn'>
): string[] {
  const en = (ann.labelEn || ann.labelCn || ann.label || '').trim()
  return en ? [en] : []
}

export function fromLabelStudioLabelArray (
  labels: string[] | undefined
): AnnotationLabelPayload & { label: string } {
  const arr = (labels ?? []).map((s) => String(s).trim()).filter(Boolean)
  if (arr.length >= 2) {
    // 兼容旧数据：如果有两个标签，第一个是中文，第二个是英文
    return {
      labelCn: arr[0],
      labelEn: arr[1],
      label: arr[0],
    }
  }
  // 新数据：只有一个英文标签
  const single = arr[0] ?? ''
  return { labelCn: '', labelEn: single, label: single }
}
